import { Box, Text } from '@factorialco/deprecated-design-system'
import { LeaveEmployeeDataFragment } from 'generated/resources/graphql'
import { sortBy } from 'lodash'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Select from 'modules/core/components/Inputs/Select'
import TextField from 'modules/core/components/TextField'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import { isDailyContract } from 'modules/timeoff/lib/contractsVersion'
import { useContractsVersion } from 'modules/timeoff/lib/contractsVersionContext'
import type Leave from 'modules/timeoff/store/models/Leave'
import type LeaveType from 'modules/timeoff/store/models/LeaveType'

interface Props {
  form: LeaveForm
  leave?: Leave | null | undefined
  availableLeaveTypes: LeaveType[]
  readOnly: boolean
  hasMedicalLeavesOpen?: (employeeId: string) => boolean
  selectedLeaveType?: LeaveType
  leaveEmployee?: LeaveEmployeeDataFragment
}

const LeaveTypeFormInput = ({
  form,
  leave,
  availableLeaveTypes,
  readOnly,
  hasMedicalLeavesOpen,
  selectedLeaveType,
  leaveEmployee,
}: Props) => {
  const { employee: loggedEmployee, canManageTimeoff } = useCurrentSession()

  const { contract } = useContractsVersion()
  const field = form.get('leave_type_id')
  const employeesManagedByCurrentUser = loggedEmployee?.reports || []

  const isEmployeeManagedByCurrentUser = employeesManagedByCurrentUser.find(
    (report) => report.id == form.get('employee_id').value
  )
  const canRequestRestrictedLeaveType =
    !!isEmployeeManagedByCurrentUser ||
    canManageTimeoff ||
    leaveEmployee?.timeoffSupervisedByCurrent

  const leaveTypes = canRequestRestrictedLeaveType
    ? availableLeaveTypes
    : availableLeaveTypes.filter((leaveType) => !leaveType.isRestricted)

  // This prevents employees from selecting leave types that don't belong to their allowances
  const leaveTypesForEmployeeAllowances = leaveEmployee?.timeoffPolicy?.id
    ? leaveTypes.filter((leaveType) => {
        const allowance = leaveType.getAllowanceForEmployee(leaveEmployee.timeoffPolicy?.id)
        return allowance !== null
      })
    : leaveTypes

  // Find the currently selected leave type from the original list (to allow display even if no active counter)
  const leaveType = leaveTypes.find((type) => String(type.attributes.id) === String(field.value))

  const sortedLeaveType = (leaveTypeToSort: LeaveType): string => {
    if (leaveTypeToSort.isHoliday) return '0'

    if (leaveTypeToSort.isMedicalLeave) return '1'

    return `3${leaveTypeToSort.name}`
  }

  /**
   * Employee only can have an open medical leave
   * In case we detect that we disabled medical_leave type
   * in selector
   * Open means: a medical leave that finish today or after and
   * is approved
   */
  const isDisabled = useUnsafeCallback(
    (leaveTypeToCheck: LeaveType): boolean => {
      const archived = !leaveTypeToCheck.attributes.active

      if (archived) return true

      if (!leaveTypeToCheck.isMedicalLeave) return false

      return hasMedicalLeavesOpen && leaveEmployee ? hasMedicalLeavesOpen(leaveEmployee.id) : false
    },
    [leaveEmployee?.id, hasMedicalLeavesOpen]
  )

  const leaveTypeCopy = (leaveTypeToCopy: LeaveType): string => {
    const accrues = leaveTypeToCopy.attributes.accrues
      ? i18n.t('settings.leave_types.attributes_info.accrues')
      : i18n.t('settings.leave_types.attributes_info.does_not_accrues')

    const approval = leaveTypeToCopy.attributes.approval_required
      ? i18n.t('settings.leave_types.attributes_info.approval')
      : i18n.t('settings.leave_types.attributes_info.no_approval')

    return i18n.t('settings.leave_types.attributes_info.sentence', {
      leave_type: leaveTypeToCopy.name,
      accrues,
      approval,
    })
  }

  const options = useUnsafeMemo(() => {
    let filteredLeaveTypes

    if (isDailyContract(contract)) {
      filteredLeaveTypes = leaveTypesForEmployeeAllowances.filter(
        (leaveTypeToFilter) =>
          !leaveTypeToFilter.isHourlyForEmployee(leaveEmployee?.timeoffPolicy?.id)
      )
    } else {
      filteredLeaveTypes = leaveTypesForEmployeeAllowances
    }

    if (leaveType && !filteredLeaveTypes.includes(leaveType)) {
      filteredLeaveTypes = [...filteredLeaveTypes, leaveType]
    }

    return sortBy(filteredLeaveTypes, sortedLeaveType).map((leaveTypeToSort) => {
      const disabled = isDisabled(leaveTypeToSort)

      const label =
        disabled && leaveTypeToSort.isMedicalLeave
          ? i18n.t('time_off.medical_leave.disabled_because_open')
          : leaveTypeCopy(leaveTypeToSort)

      return {
        value: leaveTypeToSort.attributes.id,
        label,
        disabled,
      }
    })
  }, [
    leaveEmployee?.timeoffPolicy?.id,
    isDisabled,
    leaveTypesForEmployeeAllowances,
    leaveType,
    contract,
  ])

  const label = i18n.t('time_off.type')
  const value = leaveType ? leaveTypeCopy(leaveType) : '-'

  if (!leaveType && !!leave?.attributes.medical_leave_id) {
    return null
  }

  const leaveTypeDescription = selectedLeaveType?.attributes.description

  if (readOnly) {
    return (
      <FormRow>
        <Box gap="s8" paddingTop="s12">
          <TextField readonly label={label} value={value} />
          {leaveTypeDescription && (
            <Text size="100" weight="normal">
              {leaveTypeDescription}
            </Text>
          )}
        </Box>
      </FormRow>
    )
  }

  return (
    <FormRow>
      <Box gap="s8" width="s400" paddingTop="s12">
        <Select label={label} field={field} options={options} showOriginalLabelText />
        {leaveTypeDescription && (
          <Text size="100" weight="normal">
            {leaveTypeDescription}
          </Text>
        )}
      </Box>
    </FormRow>
  )
}

export default observer(LeaveTypeFormInput)
